/**
 * Settings Dialog - Clinical Intelligence System
 * Full-featured settings modal with Integrations tab for Open Evidence connection
 */

import { FC, useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  Settings,
  Plug2,
  Shield,
  User,
  ChevronRight,
  Lock,
  Sparkles,
  CreditCard,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { IntegrationsPanel } from './IntegrationsPanel'
import { SubscriptionPanel } from './SubscriptionPanel'

type SettingsTab = 'profile' | 'subscription' | 'integrations' | 'privacy'

interface SettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultTab?: SettingsTab
}

const tabs: { id: SettingsTab; label: string; icon: typeof Settings; description: string }[] = [
  { id: 'profile', label: 'Profile', icon: User, description: 'Account information' },
  { id: 'subscription', label: 'Subscription', icon: CreditCard, description: 'Plan & billing' },
  { id: 'integrations', label: 'Integrations', icon: Plug2, description: 'Connected services' },
  { id: 'privacy', label: 'Privacy', icon: Shield, description: 'Data & security' },
]

export const SettingsDialog: FC<SettingsDialogProps> = ({
  open,
  onOpenChange,
  defaultTab = 'subscription'
}) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>(defaultTab)

  // Reset to default tab when dialog opens
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab)
    }
  }, [open, defaultTab])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col w-full max-w-3xl h-[100dvh] rounded-none p-0 gap-0 overflow-hidden bg-background/95 backdrop-blur-xl border-border/50 md:h-auto md:max-h-[85vh] md:rounded-lg">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-border/50 bg-surface/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center">
              <Settings className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-lg font-display font-semibold">Settings</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">Manage your account and preferences</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-col md:flex-row flex-1 min-h-0 md:min-h-[500px]">
          {/* Navigation — horizontal tab strip on mobile, sidebar column on desktop */}
          <nav className="flex md:flex-col gap-1 p-3 w-full md:w-56 border-b md:border-b-0 md:border-r border-border/50 bg-surface/30 overflow-x-auto md:overflow-x-visible shrink-0 scroll-fade-x md:[mask-image:none]">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-200 group shrink-0',
                    isActive
                      ? 'bg-primary/10 text-primary border border-primary/20'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  )}
                >
                  <div className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200',
                    isActive
                      ? 'bg-primary/20'
                      : 'bg-muted/50 group-hover:bg-accent'
                  )}>
                    <Icon className={cn(
                      'h-4 w-4 transition-colors',
                      isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                    )} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      'text-sm font-medium truncate',
                      isActive && 'text-primary'
                    )}>
                      {tab.label}
                    </p>
                    <p className="hidden md:block text-[10px] text-muted-foreground truncate">
                      {tab.description}
                    </p>
                  </div>
                  <ChevronRight className={cn(
                    'hidden md:block h-4 w-4 transition-all duration-200',
                    isActive
                      ? 'text-primary opacity-100 translate-x-0'
                      : 'opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0'
                  )} />
                </button>
              )
            })}

            {/* Security Badge — desktop only (the mobile tab strip has no room) */}
            <div className="hidden md:block mt-auto pt-4 border-t border-border/50">
              <div className="px-3 py-2 rounded-lg bg-clinical-sage/10 border border-clinical-sage/20">
                <div className="flex items-center gap-2 text-clinical-sage">
                  <Lock className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">HIPAA Secured</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  All data encrypted at rest
                </p>
              </div>
            </div>
          </nav>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {activeTab === 'profile' && (
              <ProfilePanel />
            )}
            {activeTab === 'subscription' && (
              <SubscriptionPanel />
            )}
            {activeTab === 'integrations' && (
              <IntegrationsPanel />
            )}
            {activeTab === 'privacy' && (
              <PrivacyPanel />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Profile Panel - Basic account info (placeholder for now)
 */
const ProfilePanel: FC = () => {
  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-clinical-teal/20 to-clinical-cyan/10 flex items-center justify-center">
          <User className="h-4 w-4 text-clinical-teal" />
        </div>
        <div>
          <h2 className="text-base font-display font-semibold">Profile</h2>
          <p className="text-xs text-muted-foreground">Your account information</p>
        </div>
      </div>

      <div className="rounded-xl border border-border/50 bg-surface/50 p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-muted/50 mx-auto mb-4 flex items-center justify-center">
          <User className="h-8 w-8 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          Profile settings coming soon
        </p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Your account is managed by your authentication provider
        </p>
      </div>
    </div>
  )
}

/**
 * Privacy Panel - Data & security settings (placeholder for now)
 */
const PrivacyPanel: FC = () => {
  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-clinical-sage/20 to-clinical-sage/10 flex items-center justify-center">
          <Shield className="h-4 w-4 text-clinical-sage" />
        </div>
        <div>
          <h2 className="text-base font-display font-semibold">Privacy & Security</h2>
          <p className="text-xs text-muted-foreground">Manage your data preferences</p>
        </div>
      </div>

      {/* Security Features */}
      <div className="space-y-3">
        <div className="rounded-xl border border-border/50 bg-surface/50 p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-clinical-teal/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Lock className="h-4 w-4 text-clinical-teal" />
            </div>
            <div>
              <h3 className="text-sm font-medium">End-to-End Encryption</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                All data is encrypted using AES-256-GCM with Azure Key Vault managed keys
              </p>
            </div>
            <div className="ml-auto">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-clinical-sage/10 text-clinical-sage text-[10px] font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                Active
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border/50 bg-surface/50 p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-clinical-cyan/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Shield className="h-4 w-4 text-clinical-cyan" />
            </div>
            <div>
              <h3 className="text-sm font-medium">HIPAA Compliance</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                90-day audit logs, automatic session timeout, secure data handling
              </p>
            </div>
            <div className="ml-auto">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-clinical-sage/10 text-clinical-sage text-[10px] font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                Compliant
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border/50 bg-surface/50 p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-clinical-amber/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkles className="h-4 w-4 text-clinical-amber" />
            </div>
            <div>
              <h3 className="text-sm font-medium">Data Retention</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Chat history and artifacts retained per HIPAA guidelines
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


